@if (layout.sidebarOpen()) {
  <div class="sidebar-backdrop" (click)="layout.closeSidebar()"></div>
}
<nav class="sidebar" [class.open]="layout.sidebarOpen()" [class.collapsed]="layout.sidebarCollapsed()">
  <div class="sidebar-header">
    <img class="app-logo" src="/branding/app-logo.png" [alt]="'shared.logoAlt' | translate" />
    <span class="app-name">{{ 'shared.logoAlt' | translate }}</span>
  </div>

  <div class="nav-search" role="search">
    <app-icon class="search-icon" name="search" [size]="16" />
    <input
      #searchInput
      pInputText
      type="text"
      autocomplete="off"
      spellcheck="false"
      [attr.aria-activedescendant]="searching() ? 'nav-result-' + activeResult() : null"
      [attr.aria-label]="'shared.nav.search.label' | translate"
      [placeholder]="'shared.nav.search.label' | translate"
      data-testid="nav-search"
      [ngModel]="query()"
      (ngModelChange)="onQueryChange($event)"
      (keydown)="onSearchKeydown($event)"
    />
    @if (query()) {
      <button
        type="button"
        class="search-clear"
        data-testid="nav-search-clear"
        [attr.aria-label]="'shared.nav.search.clear' | translate"
        (click)="clearSearch(); searchInput.focus()"
      >
        <app-icon name="x" [size]="14" />
      </button>
    }
  </div>
  <!-- The icon-only rail has no room for the field: this icon widens the sidebar and focuses it. -->
  <button
    type="button"
    class="search-rail"
    data-testid="nav-search-rail"
    [attr.title]="'shared.nav.search.label' | translate"
    [attr.aria-label]="'shared.nav.search.label' | translate"
    (click)="focusSearch()"
  >
    <app-icon name="search" [size]="18" />
  </button>

  @for (item of topItems(); track item.path) {
    <div class="nav-top">
      <a
        [routerLink]="item.path"
        routerLinkActive="active"
        class="nav-item"
        data-testid="nav-home"
        [class.kbd-active]="isHighlighted(item)"
        [attr.id]="resultId(item)"
        [attr.title]="layout.sidebarCollapsed() ? (item.labelKey | translate) : null"
        [attr.aria-label]="layout.sidebarCollapsed() ? (item.labelKey | translate) : null"
        (click)="onLinkClick()"
      >
        <app-icon [name]="item.icon" [size]="18" />
        <span class="label">{{ item.labelKey | translate }}</span>
      </a>
    </div>
  }

  <div class="nav-sections">
    @for (section of sections(); track section.id) {
      <section
        class="nav-section"
        [class.closed]="!isSectionOpen(section)"
        [class.flyout-open]="isFlyoutOpen(section)"
        [attr.data-section]="section.id"
        (mouseenter)="onGroupEnter()"
        (mouseleave)="onGroupLeave()"
      >
        @if (searching()) {
          <div class="section-title">{{ section.labelKey | translate }}</div>
        } @else {
          <button
            type="button"
            class="section-toggle"
            [class.holds-active]="sectionHoldsActivePage(section)"
            [attr.aria-expanded]="isSectionOpen(section)"
            [attr.aria-controls]="'nav-section-' + section.id"
            [attr.data-testid]="'nav-section-toggle-' + section.id"
            (click)="toggleSection(section)"
          >
            <span class="section-label">{{ section.labelKey | translate }}</span>
            <app-icon
              class="chevron flip-rtl"
              [name]="isSectionOpen(section) ? 'chevron-down' : 'chevron-right'"
              [size]="16"
            />
          </button>
        }

        <!-- The collapsed rail: one icon per group; its links open in a flyout (the section body, positioned beside the rail). -->
        <button
          type="button"
          class="group-rail"
          [class.holds-active]="sectionHoldsActivePage(section)"
          [class.open]="isFlyoutOpen(section)"
          aria-haspopup="true"
          [attr.aria-expanded]="isFlyoutOpen(section)"
          [attr.title]="section.labelKey | translate"
          [attr.aria-label]="section.labelKey | translate"
          [attr.data-testid]="'nav-group-rail-' + section.id"
          (mouseenter)="onRailEnter(section, $event)"
          (click)="onRailClick(section, $event)"
        >
          <app-icon [name]="section.icon" [size]="18" />
        </button>

        <div class="section-body" [id]="'nav-section-' + section.id">
          @for (item of section.items; track item.path) {
            <a
              [routerLink]="item.path"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: !!item.exact }"
              class="nav-item"
              [class.cluster-start]="!searching() && $index > 0 && item.cluster !== section.items[$index - 1].cluster"
              [class.kbd-active]="isHighlighted(item)"
              [attr.id]="resultId(item)"
              [attr.title]="layout.sidebarCollapsed() ? (item.labelKey | translate) : null"
              [attr.aria-label]="layout.sidebarCollapsed() ? (item.labelKey | translate) : null"
              (click)="onLinkClick()"
            >
              <app-icon [name]="item.icon" [size]="16" />
              <span class="label">{{ item.labelKey | translate }}</span>
            </a>
          }
        </div>

        @if (isFlyoutOpen(section)) {
          <app-sidebar-flyout
            [title]="section.labelKey"
            [items]="section.items"
            [top]="flyoutPos().top"
            [start]="flyoutPos().start"
            (linkClick)="onLinkClick()"
          />
        }
      </section>
    }
    @if (shortcutResults().length > 0) {
      <section class="nav-section" data-section="shortcuts" data-testid="nav-shortcut-results">
        <div class="section-body">
          @for (item of shortcutResults(); track item.path) {
            <a
              [routerLink]="item.path"
              routerLinkActive="active"
              class="nav-item"
              [class.kbd-active]="isHighlighted(item)"
              [attr.id]="resultId(item)"
              (click)="onLinkClick()"
            >
              <app-icon [name]="item.icon" [size]="16" />
              <span class="label">{{ item.labelKey | translate }}</span>
            </a>
          }
        </div>
      </section>
    }
    @if (searching() && topItems().length === 0 && sections().length === 0 && shortcutResults().length === 0 && pinnedItems().length === 0) {
      <p class="nav-empty" role="status" data-testid="nav-search-empty">{{ 'shared.nav.search.empty' | translate }}</p>
    }
  </div>

  @for (item of pinnedItems(); track item.path) {
    <div class="nav-pinned" data-testid="nav-pinned">
              <a
                [routerLink]="item.path"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: !!item.exact }"
                class="nav-item"
                [class.kbd-active]="isHighlighted(item)"
                [attr.id]="resultId(item)"
                [attr.title]="layout.sidebarCollapsed() ? (item.labelKey | translate) : null"
                [attr.aria-label]="layout.sidebarCollapsed() ? (item.labelKey | translate) : null"
                (click)="onLinkClick()"
              >
                <app-icon [name]="item.icon" [size]="18" />
                <span class="label">{{ item.labelKey | translate }}</span>
              </a>
    </div>
  }

  <button
    type="button"
    class="collapse-toggle"
    [attr.title]="layout.sidebarCollapsed() ? ('shared.nav.expandMenu' | translate) : null"
    [attr.aria-label]="layout.sidebarCollapsed() ? ('shared.nav.expandMenu' | translate) : null"
    (click)="closeFlyout(); layout.toggleSidebarCollapsed()"
  >
    <app-icon [name]="layout.sidebarCollapsed() ? 'chevron-right' : 'chevron-left'" [size]="18" class="flip-rtl" />
    <span class="label">{{ (layout.sidebarCollapsed() ? 'shared.nav.expandMenu' : 'shared.nav.collapseMenu') | translate }}</span>
  </button>
</nav>
